import { FunctionComponent, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Button, Flex, Text, List, ListItem, Skeleton } from '@chakra-ui/react';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { HomeFeaturesComponents } from '@/model';
import { translateApplicationComponentId } from '@/model/application-component';
import { useTranslation } from 'react-i18next';

type HomeAreaFeaturesProps = {
  isLoading?: boolean;
  teamUUID?: string;
  available: HomeFeaturesComponents[];
  selected: HomeFeaturesComponents[];
  onChange: (selected: HomeFeaturesComponents[]) => void;
};

export const HomeAreaFeatures: FunctionComponent<HomeAreaFeaturesProps> = props => {
  const { isLoading, available = [], selected = [], onChange, teamUUID } = props;
  const prevTeamUuidRef = useRef<string>();

  const [internalListAvailable, setInternalListAvailable] = useState<HomeFeaturesComponents[]>(available || []);
  const [internalSelected, setInternalSelected] = useState<HomeFeaturesComponents[]>(selected || []);
  const { t } = useTranslation();

  const [selectedAvailableItemId, setSelectedAvailableItemId] = useState('');
  const [selectedActiveItemId, setSelectedActiveItemId] = useState('');

  const isAddButtonDisabled = useMemo(() => {
    return !(selectedAvailableItemId && !selectedActiveItemId);
  }, [selectedAvailableItemId, selectedActiveItemId]);

  const isRemoveButtonDisabled = useMemo(() => {
    return !(selectedActiveItemId && !selectedAvailableItemId);
  }, [selectedActiveItemId, selectedAvailableItemId]);

  const isReplaceButtonDisabled = useMemo(() => {
    return !(selectedAvailableItemId && selectedActiveItemId);
  }, [selectedAvailableItemId, selectedActiveItemId]);

  const isMoveUpButtonDisabled = useMemo(() => {
    if (selectedActiveItemId && internalSelected[0]?.id + (internalSelected[0]?.resource_uuid || '') !== selectedActiveItemId) {
      return false;
    }
    return true;
  }, [internalSelected, selectedActiveItemId]);

  const isMoveDownButtonDisabled = useMemo(() => {
    if (
      selectedActiveItemId &&
      internalSelected[internalSelected.length - 1]?.id + (internalSelected[internalSelected.length - 1]?.resource_uuid || '') !==
        selectedActiveItemId
    ) {
      return false;
    }
    return true;
  }, [internalSelected, selectedActiveItemId]);

  const handleAddToSelected = useCallback(() => {
    if (isAddButtonDisabled) return;
    const index = internalListAvailable.findIndex(item => item.id + (item.resource_uuid || '') === selectedAvailableItemId);
    if (index === -1) return;

    isDirty.current = true; // ✅ usuário interagiu

    const itemToMove = internalListAvailable[index];
    const updatedAvailable = [...internalListAvailable];
    updatedAvailable.splice(index, 1);

    const updatedSelected = [...internalSelected, itemToMove];

    setInternalListAvailable(updatedAvailable);
    setInternalSelected(updatedSelected);
    setSelectedAvailableItemId('');
    setSelectedActiveItemId('');
    onChange?.(updatedSelected);
  }, [internalListAvailable, internalSelected, isAddButtonDisabled, onChange, selectedAvailableItemId]);

  const handleRemoveToInternal = useCallback(() => {
    if (isRemoveButtonDisabled) return;

    const index = internalSelected.findIndex(item => item.id + (item.resource_uuid || '') === selectedActiveItemId);

    if (index === -1) return;

    isDirty.current = true; // ✅ usuário interagiu

    const itemToMove = internalSelected[index];

    const updatedSelected = [...internalSelected];
    updatedSelected.splice(index, 1); // ❗ remove da lista ativa

    const updatedAvailable = [...internalListAvailable, itemToMove]; // adiciona na lista disponível

    setInternalSelected(updatedSelected);
    setInternalListAvailable(updatedAvailable);
    setSelectedActiveItemId('');
    setSelectedAvailableItemId('');
    onChange?.(updatedSelected);
  }, [internalListAvailable, internalSelected, isRemoveButtonDisabled, onChange, selectedActiveItemId]);

  const handleReplace = useCallback(() => {
    if (isReplaceButtonDisabled) return;

    const availableIndex = internalListAvailable.findIndex(
      item => item.id + (item.resource_uuid || '') === selectedAvailableItemId,
    );
    const selectedIndex = internalSelected.findIndex(item => item.id + (item.resource_uuid || '') === selectedActiveItemId);

    if (availableIndex === -1 || selectedIndex === -1) return;

    isDirty.current = true; // ✅ usuário interagiu

    const itemFromAvailable = internalListAvailable[availableIndex];
    const itemFromSelected = internalSelected[selectedIndex];

    const updatedAvailable = [...internalListAvailable];
    updatedAvailable.splice(availableIndex, 1, itemFromSelected);

    const updatedSelected = [...internalSelected];
    updatedSelected.splice(selectedIndex, 1, itemFromAvailable);

    setInternalListAvailable(updatedAvailable);
    setInternalSelected(updatedSelected);
    setSelectedAvailableItemId('');
    setSelectedActiveItemId('');
    onChange?.(updatedSelected);
  }, [
    internalListAvailable,
    internalSelected,
    isReplaceButtonDisabled,
    onChange,
    selectedActiveItemId,
    selectedAvailableItemId,
  ]);

  const handleMoveSelectedUp = useCallback(() => {
    const isItemOnSelectedList = Boolean(
      internalSelected.find(item => item.id + (item.resource_uuid || '') === selectedActiveItemId),
    );

    if (!isItemOnSelectedList) return;

    isDirty.current = true; // ✅ usuário interagiu

    const updatedSelected = [...internalSelected];
    const itemToMoveIndex = internalSelected.findIndex(item => item.id + (item.resource_uuid || '') === selectedActiveItemId);

    const itemAbove = updatedSelected[itemToMoveIndex - 1];
    const itemToMove = updatedSelected[itemToMoveIndex];

    updatedSelected[itemToMoveIndex] = itemAbove;
    updatedSelected[itemToMoveIndex - 1] = itemToMove;

    setInternalSelected(updatedSelected);
    onChange?.(updatedSelected);
  }, [internalSelected, onChange, selectedActiveItemId]);

  const handleMoveSelectedDown = useCallback(() => {
    const isItemOnSelectedList = Boolean(
      internalSelected.find(item => item.id + (item.resource_uuid || '') === selectedActiveItemId),
    );

    if (!isItemOnSelectedList) return;

    isDirty.current = true; // ✅ usuário interagiu

    const updatedSelected = [...internalSelected];
    const itemToMoveIndex = internalSelected.findIndex(item => item.id + (item.resource_uuid || '') === selectedActiveItemId);

    const itemUnder = updatedSelected[itemToMoveIndex + 1];
    const itemToMove = updatedSelected[itemToMoveIndex];

    updatedSelected[itemToMoveIndex] = itemUnder;
    updatedSelected[itemToMoveIndex + 1] = itemToMove;

    setInternalSelected(updatedSelected);
    onChange?.(updatedSelected);
  }, [internalSelected, onChange, selectedActiveItemId]);

  const isDirty = useRef(false);

  useEffect(() => {
    const teamChanged = prevTeamUuidRef.current && prevTeamUuidRef.current !== teamUUID;

    if (!isDirty.current || teamChanged) {
      isDirty.current = false; // limpa dirty se trocar de time
      setInternalListAvailable(available);
      setInternalSelected(selected);
    }

    prevTeamUuidRef.current = teamUUID;
  }, [available, selected, teamUUID]);

  return (
    <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
      <Flex direction='column' gap={2}>
        <Text fontWeight='medium' fontSize='12px'>
          {t('pages.application_home.home_area_features.available_label')}
        </Text>

        <Flex gap={6}>
          {isLoading ? (
            <Skeleton minH='300px' w='100%' minW='190px' maxW='220px' borderRadius='8px' />
          ) : (
            <Flex
              direction='column'
              as={List}
              w='100%'
              minW='190px'
              maxW='220px'
              maxH={'500px'}
              minH={'400px'}
              border='1px solid black'
              borderRadius='8px'
              py={2}
              px={0}
              overflowY='scroll'
            >
              {(internalListAvailable || [])
                ?.sort((a, b) => {
                  if (a.resource_name && b.resource_name) {
                    return a.resource_name.localeCompare(b.resource_name);
                  }

                  return translateApplicationComponentId(a.id).localeCompare(translateApplicationComponentId(b.id));
                })
                .map(item => {
                  const isSelected = item?.id + (item?.resource_uuid || '') === selectedAvailableItemId;

                  return (
                    <ListItem
                      key={item?.id + (item?.resource_uuid || '')}
                      py={2}
                      px={4}
                      fontWeight='medium'
                      backgroundColor={isSelected ? 'brand.500' : ''}
                      color={isSelected ? 'white' : 'text.main'}
                      cursor='pointer'
                      onClick={() => {
                        setSelectedAvailableItemId(isSelected ? '' : item.id + (item.resource_uuid || ''));
                      }}
                    >
                      <Flex direction='column' gap={0}>
                        <Text color={isSelected ? 'white' : 'text.main'}>{translateApplicationComponentId(item.id)}</Text>
                        <Text color={isSelected ? 'white' : 'text.main'} fontSize={12} opacity={0.5}>
                          {item.resource_name}
                        </Text>
                      </Flex>
                    </ListItem>
                  );
                })}
            </Flex>
          )}

          <Flex direction='column' gap={4} alignSelf='center'>
            <Button
              disabled={isAddButtonDisabled}
              variant='outline'
              w='40px'
              h='40px'
              borderColor={isAddButtonDisabled ? 'gray.500' : 'brand.500'}
              cursor={isAddButtonDisabled ? 'not-allowed!important' : ''}
              _hover={{ backgroundColor: 'transparent' }}
              onClick={handleAddToSelected}
            >
              <ChakraFeatherIcon name='plus' color={isAddButtonDisabled ? 'gray.500' : 'brand.500'} />
            </Button>

            <Button
              disabled={isRemoveButtonDisabled}
              variant='outline'
              w='40px'
              h='40px'
              borderColor={isRemoveButtonDisabled ? 'gray.500' : 'brand.500'}
              cursor={isRemoveButtonDisabled ? 'not-allowed!important' : ''}
              _hover={{ backgroundColor: 'transparent' }}
              onClick={handleRemoveToInternal}
            >
              <ChakraFeatherIcon name='minus' color={isRemoveButtonDisabled ? 'gray.500' : 'brand.500'} />
            </Button>

            <Button
              disabled={isReplaceButtonDisabled}
              variant='outline'
              w='40px'
              h='40px'
              borderColor={isReplaceButtonDisabled ? 'gray.500' : 'brand.500'}
              cursor={isReplaceButtonDisabled ? 'not-allowed!important' : ''}
              _hover={{ backgroundColor: 'transparent' }}
              onClick={handleReplace}
              aria-label={t('pages.application_home.home_area_features.replace_label')}
            >
              <ChakraFeatherIcon name='repeat' color={isReplaceButtonDisabled ? 'gray.500' : 'brand.500'} />
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <Flex direction='column' gap={2}>
        <Text fontWeight='medium' fontSize='12px'>
          {t('pages.application_home.home_area_features.selected_label')}
        </Text>

        <Flex gap={6}>
          {isLoading ? (
            <Skeleton minH='300px' w='100%' minW='190px' maxW='220px' borderRadius='8px' />
          ) : (
            <Flex
              direction='column'
              as={List}
              w='100%'
              minW='190px'
              maxW='220px'
              maxH={'500px'}
              minH={'400px'}
              border='1px solid black'
              borderRadius='8px'
              py={2}
              px={0}
              overflowY={'scroll'}
            >
              {internalSelected?.map(item => {
                const isSelected = item?.id + (item?.resource_uuid || '') === selectedActiveItemId;

                return (
                  <ListItem
                    key={item?.id + (item?.resource_uuid || '')}
                    py={2}
                    px={4}
                    fontWeight='medium'
                    backgroundColor={isSelected ? 'brand.500' : ''}
                    color={isSelected ? 'white' : 'text.main'}
                    cursor='pointer'
                    onClick={() => {
                      setSelectedActiveItemId(isSelected ? '' : item.id + (item.resource_uuid || ''));
                    }}
                  >
                    <Flex direction='column' gap={0}>
                      <Text color={isSelected ? 'white' : 'text.main'}>{translateApplicationComponentId(item.id)}</Text>
                      <Text color={isSelected ? 'white' : 'text.main'} fontSize={12} opacity={0.5}>
                        {item.resource_name}
                      </Text>
                    </Flex>
                  </ListItem>
                );
              })}
            </Flex>
          )}

          <Flex direction='column' gap={4} alignSelf='center'>
            <Button
              disabled={isMoveUpButtonDisabled}
              variant='outline'
              w='40px'
              h='40px'
              borderColor={isMoveUpButtonDisabled ? 'gray.500' : 'brand.500'}
              cursor={isMoveUpButtonDisabled ? 'not-allowed!important' : ''}
              _hover={{ backgroundColor: 'transparent' }}
              onClick={handleMoveSelectedUp}
            >
              <ChakraFeatherIcon name='chevron-up' color={isMoveUpButtonDisabled ? 'gray.500' : 'brand.500'} />
            </Button>

            <Button
              disabled={isMoveDownButtonDisabled}
              variant='outline'
              w='40px'
              h='40px'
              borderColor={isMoveDownButtonDisabled ? 'gray.500' : 'brand.500'}
              cursor={isMoveDownButtonDisabled ? 'not-allowed!important' : ''}
              _hover={{ backgroundColor: 'transparent' }}
              onClick={handleMoveSelectedDown}
            >
              <ChakraFeatherIcon name='chevron-down' color={isMoveDownButtonDisabled ? 'gray.500' : 'brand.500'} />
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
